import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, addMonths } from 'date-fns'
import { useAuth } from '../context/AuthContext'
import { firmDateIso } from '../utils/eventTime'
import { solDaysLeft, formatDateOnly as fmtDateOnly } from '../utils/solDays'
import useFirmTimezone from '../hooks/useFirmTimezone'
import { usePageShortcut } from '../hooks/useKeyboardShortcuts'
import api, { getMatters } from '../services/api'
import EmptyState from '../components/EmptyState'
import { usePendingDelete } from '../hooks/usePendingDelete'
import useFieldIds from '../hooks/useFieldIds'
import HelpButton from '../components/HelpButton'

const CA_SOL_PERIODS = [
  { label: 'Personal Injury', months: 24 },
  { label: 'Wrongful Death', months: 24 },
  { label: 'Medical Malpractice', months: 36 },
  { label: 'Property Damage', months: 36 },
  { label: 'Breach of Written Contract', months: 48 },
  { label: 'Breach of Oral Contract', months: 24 },
  { label: 'Fraud', months: 36 },
  { label: 'Defamation', months: 12 },
  { label: 'False Imprisonment', months: 24 },
  { label: 'Professional Negligence', months: 24 },
  { label: 'Products Liability', months: 24 },
  { label: 'Construction Defect', months: 36 },
  { label: 'Employment Discrimination', months: 36 },
  { label: 'Civil Rights', months: 24 },
  { label: 'Elder Abuse', months: 24 },
  { label: 'Sexual Assault', months: 36 },
  { label: 'Government Claim (Tort)', months: 6 },
  { label: 'Breach of Fiduciary Duty', months: 36 },
  { label: 'Other', months: null },
]

// sol_date (and incident_date) are Postgres DATE columns holding the firm's
// day.  Days until the SOL are FIRM calendar days and a date shows its own
// calendar day: utils/solDays.js (solDaysLeft, formatDateOnly), shared with
// the Matter page's SOL section.  This used date-fns
// differenceInDays(new Date(sol_date), new Date()): elapsed 24-hour periods
// from UTC midnight, so the count was a day short after 5 PM Pacific (an SOL
// due today read "Expired 1d ago"), and `new Date(sol_date)` showed the SOL
// Date column a day early in US zones (James, 2026-10-17;
// SOLTracker.firmDay.test.js).

const getSOLPriority = (entry) => {
  if (entry.status === 'filed' || entry.status === 'closed') return { label: 'Filed', color: 'bg-gray-100 text-gray-500', badge: 'bg-gray-400', hint: 'Filed or closed — SOL no longer applies' }
  if (entry.is_tolled) return { label: 'Tolled', color: 'bg-blue-100 text-blue-700', badge: 'bg-blue-400', hint: 'Tolled — SOL clock is paused (see tolling notes)' }
  const days = solDaysLeft(entry)
  if (days === null) return { label: 'No date', color: 'bg-gray-100 text-gray-500', badge: 'bg-gray-400', hint: 'No SOL date recorded — edit the entry to add one' }
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, color: 'bg-gray-900 text-white', badge: 'bg-gray-900', hint: `SOL EXPIRED ${Math.abs(days)} days ago — file immediately or the claim is barred` }
  if (days <= 30) return { label: `${days} days`, color: 'bg-red-600 text-white', badge: 'bg-red-600', hint: `CRITICAL: ${days} days until SOL expires — file within 30 days` }
  if (days <= 90) return { label: `${days} days`, color: 'bg-orange-100 text-orange-700', badge: 'bg-orange-500', hint: `Urgent: ${days} days until SOL — inside the 90-day window, start filing prep` }
  if (days <= 180) return { label: `${days} days`, color: 'bg-yellow-100 text-yellow-700', badge: 'bg-yellow-500', hint: `Warning: ${days} days until SOL — inside 180-day window, monitor closely` }
  return { label: `${days} days`, color: 'bg-green-100 text-green-700', badge: 'bg-green-500', hint: `Safe: ${days} days until SOL (>180 days)` }
}

const emptyForm = {
  matter_id: '', cause_of_action: '', incident_date: '', sol_period_months: '',
  sol_date: '', tolling_notes: '', is_tolled: false, tolled_date: '', tolling_reason: '', status: 'active'
}

export default function SOLTracker() {
  const { user } = useAuth()
  // The firm zone for "today" in every day count below — fetched when this
  // page is a pop-out (PopupLayout never sets it), re-rendering once known.
  useFirmTimezone()
  const canEdit = (user?.role === 'admin' || user?.permissions?.is_admin) || user?.permissions?.can_create_sol
  const pending = usePendingDelete()
  const [searchParams, setSearchParams] = useSearchParams()
  const [entries, setEntries] = useState([])
  const [matters, setMatters] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ ...emptyForm })
  const fid = useFieldIds()

  // `n` opens the new-entry form (gated by canEdit — no point offering
  // the shortcut to users who'd just see a 403).
  usePageShortcut('n', () => { if (canEdit) { setEditingId(null); setForm({ ...emptyForm }); setShowForm(true) } })

  useEffect(() => { fetchData() }, [])

  // ?new=1 from Ctrl+K "New SOL entry"
  useEffect(() => {
    if (searchParams.get('new') === '1' && canEdit) {
      setShowForm(true)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams, canEdit])

  const fetchData = async () => {
    try {
      const [solRes, mattersRes] = await Promise.all([
        api.get('/api/sol'),
        getMatters({ slim: true })
      ])
      setEntries(solRes.data)
      setMatters(mattersRes.data)
    } catch (err) {
      setError(err.response?.data?.error || 'Error loading data')
    } finally {
      setLoading(false)
    }
  }

  const handleCauseChange = (cause) => {
    const period = CA_SOL_PERIODS.find(p => p.label === cause)
    const newForm = { ...form, cause_of_action: cause, sol_period_months: period?.months || '' }
    if (period?.months && form.incident_date) {
      newForm.sol_date = format(addMonths(new Date(form.incident_date + 'T12:00:00'), period.months), 'yyyy-MM-dd')
    }
    setForm(newForm)
  }

  const handleIncidentDateChange = (date) => {
    const newForm = { ...form, incident_date: date }
    if (date && form.sol_period_months) {
      newForm.sol_date = format(addMonths(new Date(date + 'T12:00:00'), parseInt(form.sol_period_months)), 'yyyy-MM-dd')
    }
    setForm(newForm)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    try {
      if (editingId) {
        await api.put(`/api/sol/${editingId}`, form)
        setEditingId(null)
      } else {
        await api.post('/api/sol', form)
      }
      setForm({ ...emptyForm })
      setShowForm(false)
      fetchData()
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving SOL entry')
    }
  }

  const handleEdit = (entry) => {
    setEditingId(entry.id)
    setForm({
      matter_id: entry.matter_id || '',
      cause_of_action: entry.cause_of_action || '',
      incident_date: entry.incident_date ? entry.incident_date.slice(0, 10) : '',
      sol_period_months: entry.sol_period_months || '',
      sol_date: entry.sol_date ? entry.sol_date.slice(0, 10) : '',
      tolling_notes: entry.tolling_notes || '',
      is_tolled: entry.is_tolled || false,
      tolled_date: entry.tolled_date ? entry.tolled_date.slice(0, 10) : '',
      tolling_reason: entry.tolling_reason || '',
      status: entry.status || 'active'
    })
    setShowForm(true)
    window.scrollTo(0, 0)
  }

  const handleDelete = (id) => {
    const target = entries.find(e => e.id === id)
    if (!target) return
    pending.schedule({
      url: `/api/sol/${id}`,
      message: 'SOL entry deleted',
      onOptimisticRemove: () => setEntries(prev => prev.filter(e => e.id !== id)),
      onRestore: () => setEntries(prev => [target, ...prev]),
      onDeleteError: (err) => setError(err.response?.data?.error || 'Error deleting SOL entry'),
    })
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-500">Loading SOL tracker...</p>
    </div>
  )

  const active = entries.filter(e => e.status === 'active' && !e.is_tolled)
  // Same firm-day count as each row's chip, so a card never disagrees with
  // the table under it.  An entry with no readable date counts in none.
  const critical = active.filter(e => { const d = solDaysLeft(e); return d !== null && d >= 0 && d <= 30 })
  const urgent = active.filter(e => { const d = solDaysLeft(e); return d !== null && d > 30 && d <= 90 })
  const warning = active.filter(e => { const d = solDaysLeft(e); return d !== null && d > 90 && d <= 180 })
  const safe = active.filter(e => { const d = solDaysLeft(e); return d !== null && d > 180 })

  return (
    <div>
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Statute of Limitations Tracker<HelpButton slug="sol" /></h2>
          <p className="text-sm text-gray-500 mt-1">California SOL Periods</p>
        </div>
        {canEdit && <button onClick={() => { setShowForm(!showForm); setEditingId(null); setForm({ ...emptyForm }) }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
          + Add SOL Entry
        </button>}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border-l-4 border-red-500 p-4">
          <p className="text-xs text-gray-500 mb-1">Critical (30 days)</p>
          <p className="text-2xl font-bold text-red-600">{critical.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border-l-4 border-orange-500 p-4">
          <p className="text-xs text-gray-500 mb-1">Urgent (90 days)</p>
          <p className="text-2xl font-bold text-orange-600">{urgent.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border-l-4 border-yellow-500 p-4">
          <p className="text-xs text-gray-500 mb-1">Warning (180 days)</p>
          <p className="text-2xl font-bold text-yellow-600">{warning.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border-l-4 border-green-500 p-4">
          <p className="text-xs text-gray-500 mb-1">Safe (180+ days)</p>
          <p className="text-2xl font-bold text-green-600">{safe.length}</p>
        </div>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit SOL Entry' : 'Add SOL Entry'}</h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor={fid('matter')} className="block text-sm font-medium text-gray-700 mb-1">Matter</label>
              <select id={fid('matter')} required value={form.matter_id} disabled={!!editingId}
                onChange={e => setForm({...form, matter_id: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select matter</option>
                {matters.filter(m => editingId || m.status === 'active')
                  .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                  .map(m => (
                    <option key={m.id} value={m.id}>{m.first_name} {m.last_name} — {m.title}</option>
                  ))}
              </select>
            </div>
            <div>
              <label htmlFor={fid('cause')} className="block text-sm font-medium text-gray-700 mb-1">Cause of Action</label>
              <select id={fid('cause')} required value={form.cause_of_action}
                onChange={e => handleCauseChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="">Select cause of action</option>
                {CA_SOL_PERIODS.map(p => (
                  <option key={p.label} value={p.label}>{p.label}{p.months ? ` (${p.months} mo)` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor={fid('sol-period')} className="block text-sm font-medium text-gray-700 mb-1">SOL Period (months)</label>
              <input id={fid('sol-period')} type="number" min="1" value={form.sol_period_months}
                onChange={e => {
                  const months = e.target.value
                  const newForm = { ...form, sol_period_months: months }
                  if (months && form.incident_date) {
                    newForm.sol_date = format(addMonths(new Date(form.incident_date + 'T12:00:00'), parseInt(months)), 'yyyy-MM-dd')
                  }
                  setForm(newForm)
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('incident-date')} className="block text-sm font-medium text-gray-700 mb-1">Incident Date</label>
              <input id={fid('incident-date')} type="date" value={form.incident_date}
                max={firmDateIso()}
                onChange={e => handleIncidentDateChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div>
              <label htmlFor={fid('sol-date')} className="block text-sm font-medium text-gray-700 mb-1">SOL Expiration Date</label>
              <input id={fid('sol-date')} type="date" required value={form.sol_date}
                onChange={e => setForm({...form, sol_date: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 font-semibold" />
            </div>
            <div>
              <label htmlFor={fid('sol-status')} className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select id={fid('sol-status')} value={form.status}
                onChange={e => setForm({...form, status: e.target.value})}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option value="active">Active</option>
                <option value="filed">Filed</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input type="checkbox" checked={form.is_tolled}
                  onChange={e => setForm({...form, is_tolled: e.target.checked})}
                  className="rounded" />
                SOL is Tolled
              </label>
            </div>
            {form.is_tolled && (
              <>
                <div>
                  <label htmlFor={fid('tolled-date')} className="block text-sm font-medium text-gray-700 mb-1">Tolled Date</label>
                  <input id={fid('tolled-date')} type="date" value={form.tolled_date}
                    onChange={e => setForm({...form, tolled_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
                <div className="md:col-span-2">
                  <label htmlFor={fid('tolling-reason')} className="block text-sm font-medium text-gray-700 mb-1">Tolling Reason</label>
                  <input id={fid('tolling-reason')} type="text" value={form.tolling_reason}
                    onChange={e => setForm({...form, tolling_reason: e.target.value})}
                    placeholder="Reason for tolling"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
                </div>
              </>
            )}
            <div className="md:col-span-3">
              <label htmlFor={fid('sol-notes')} className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea id={fid('sol-notes')} value={form.tolling_notes} rows={2}
                onChange={e => setForm({...form, tolling_notes: e.target.value})}
                placeholder="Additional notes..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>
            <div className="md:col-span-3 flex gap-3">
              <button type="submit"
                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                {editingId ? 'Save Changes' : 'Add SOL Entry'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingId(null) }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        {entries.length === 0 ? (
          <EmptyState
            icon="⚖️"
            title="No SOL entries tracked yet"
            description="Track statute-of-limitations deadlines for every case.  The system auto-calculates SOL dates from the incident + tort type and warns you as deadlines approach."
            action={canEdit ? { label: '+ New SOL Entry', onClick: () => { setEditingId(null); setForm({ ...emptyForm }); setShowForm(true) } } : undefined}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Matter</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Client</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Cause of Action</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Incident Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">SOL Date</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Days Left</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Attorney</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {entries.map(entry => {
                  const priority = getSOLPriority(entry)
                  return (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">
                        {entry.matter_title}
                        {entry.file_number && <span className="text-xs text-gray-400 ml-1">#{entry.file_number}</span>}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{entry.first_name} {entry.last_name}</td>
                      <td className="px-4 py-3 text-gray-700">{entry.cause_of_action}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {fmtDateOnly(entry.incident_date, 'MMM d, yyyy') || '—'}
                      </td>
                      <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">
                        {fmtDateOnly(entry.sol_date, 'MMM d, yyyy') || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          title={priority.hint}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium cursor-help ${priority.color}`}>
                          {priority.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          entry.is_tolled ? 'bg-blue-100 text-blue-700' :
                          entry.status === 'filed' ? 'bg-green-100 text-green-700' :
                          entry.status === 'closed' ? 'bg-gray-100 text-gray-500' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {entry.is_tolled ? 'Tolled' : entry.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{entry.responsible_attorney_name || '—'}</td>
                      <td className="px-4 py-3">
                        {canEdit && <div className="flex gap-2">
                          <button onClick={() => handleEdit(entry)}
                            className="text-xs text-blue-500 hover:underline">Edit</button>
                          <button onClick={() => handleDelete(entry.id)}
                            className="text-xs text-red-400 hover:underline">Delete</button>
                        </div>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
